import React, { useState, useMemo } from 'react';
import { useProcessContext } from '../../context/ProcessContext';
import { useTheme } from '../../context/ThemeContext';
import ProcessRow from './ProcessRow';
import { headCells, getComparator } from './ProcessUtils';
import { getProcessVisualizerStyles } from './ProcessStyles';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from '@mui/material';

/**
 * Main Process Visualizer component
 * Displays a table of process data with sorting, filtering, and expandable details
 * 
 * @returns {JSX.Element}
 */
function ProcessVisualizer({ serverId }) {
  const { processes: processesMap, loading, error } = useProcessContext();
  const { isDarkTheme } = useTheme();
  const styles = getProcessVisualizerStyles(isDarkTheme);
  
  // Local state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  /**
   * Handle sort request
   * @param {Event} event - Click event
   * @param {string} property - Column property to sort by
   */
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Handle search input change
   * @param {Event} event - Change event
   */
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  /**
   * Handle status filter change
   * @param {Event} event - Change event
   */
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  /**
   * Handle row selection
   * @param {string} processKey - Unique key of the selected process (either process_id or composite key)
   */
  const handleSelect = (processKey) => {
    setSelected(selected === processKey ? null : processKey);
  };

  /**
   * Filter and sort processes based on current filters and sort settings
   */
  const filteredAndSortedProcesses = useMemo(() => {
    // Convert map to array for processing
    let processesArray = Object.values(processesMap);

    // Filter by server ID if provided
    if (serverId) {
      processesArray = processesArray.filter(p => 
        p.server_id?.toLowerCase() === serverId.toLowerCase()
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      processesArray = processesArray.filter(p => (p.ui_state || p.state) === statusFilter);
    }
    
    // Filter by search term
    if (searchTerm) {
      processesArray = processesArray.filter(p =>
        p?.Process?.process_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort the array
    return processesArray.sort(getComparator(order, orderBy));
  }, [processesMap, serverId, order, orderBy, searchTerm, statusFilter]);

  // Display loading state
  if (loading && Object.keys(processesMap).length === 0) {
    return <Box sx={styles.container}><CircularProgress /></Box>;
  }

  // Display error state
  if (error) {
    return (
      <Box sx={styles.container}>
        <Alert severity="error">
          Error loading process data: {error.message || JSON.stringify(error)}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper}>
        {/* Toolbar with search and filter */}
        <Box sx={styles.toolbarContainer}>
          <TextField
            label="Search by Name"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={styles.searchField}
          />
          <FormControl size="small" sx={styles.statusFilter}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={handleStatusFilterChange}
              MenuProps={styles.menuProps}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Process table */}
        <TableContainer>
          <Table aria-labelledby="tableTitle" size="medium">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" /> {/* For expand icon */}
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    padding={headCell.disablePadding ? 'none' : 'normal'}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={(event) => handleRequestSort(event, headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={{
                          border: 0,
                          clip: 'rect(0 0 0 0)',
                          height: 1,
                          margin: -1,
                          overflow: 'hidden',
                          padding: 0,
                          position: 'absolute',
                          top: 20,
                          width: 1
                        }}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedProcesses.map((processData, idx) => {
                // Only proceed if we have valid Process data
                if (!processData?.Process) return null;
                // Always use Process.name as the unique key
                const monitorName = processData.Process.name;
                if (!monitorName) return null;
                return (
                  <ProcessRow
                    key={monitorName}
                    processData={processData}
                    isItemSelected={selected === monitorName}
                    onSelect={() => handleSelect(monitorName)}
                  />
                );
              })}
              {filteredAndSortedProcesses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No processes found matching your criteria.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default ProcessVisualizer;
